import styled, { createGlobalStyle } from 'styled-components'

/** Applied to antd Select popup via `popupClassName`; required when the menu is portaled outside `.select-dropdown` (e.g. `getPopupContainer={() => document.body}`). */
export const DS_SELECT_POPUP_CLASS = 'ds-select-popup'
export const DS_SELECT_POPUP_MULTIPLE_CLASS = 'ds-select-popup--multiple'

/**
 * Dropdown menus portaled to `document.body` are not descendants of `.select-dropdown`, so scoped
 * rules under SelectStyleWrapper do not apply. These globals mirror `.ant-select-item-option-selected`
 * styling from the wrapped select.
 */
export const DesignSystemSelectPopupGlobalStyle = createGlobalStyle`
  .${DS_SELECT_POPUP_CLASS}.ant-select-dropdown
    .ant-select-item.ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    color: var(--primary);
    background-color: var(--ds-blue-8);
  }

  .${DS_SELECT_POPUP_CLASS}.ant-select-dropdown
    .ant-select-item.ant-select-item-option-selected:not(.ant-select-item-option-disabled)
    .option_label {
    font-weight: var(--semibold);
  }

  .${DS_SELECT_POPUP_CLASS}.ant-select-dropdown
    .ant-select-item-option-selected.ant-select-item-option-active:not(
      .ant-select-item-option-disabled
    ) {
    background-color: var(--dropdown-selected);
  }

  .${DS_SELECT_POPUP_CLASS}.${DS_SELECT_POPUP_MULTIPLE_CLASS}.ant-select-dropdown
    .ant-select-item.ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    color: var(--primary-color);
    background-color: transparent;
  }

  .${DS_SELECT_POPUP_CLASS}.${DS_SELECT_POPUP_MULTIPLE_CLASS}.ant-select-dropdown
    .ant-select-item.ant-select-item-option-selected:not(.ant-select-item-option-disabled)
    .option_label {
    font-weight: var(--regular);
  }
`

// selectors for build own select
// drop-list = .ant-select-dropdown
// item = .ant-select-item

export const BORDER_RADIUS = '8px'
export const BORDER_COLOR = `1px solid var(--input-border-color);`
const INPUT_HEIGHT = '32px'

export const SelectStyleWrapper = styled.div`
  // selector input
  &.custom-time,
  &.day-picker {
    input {
      border-radius: ${BORDER_RADIUS} !important;
      border: ${BORDER_COLOR} !important;
      height: ${INPUT_HEIGHT} !important;
      padding: 0 12px;
      color: var(--primary-color);

      &:focus {
        border: 1px solid var(--primary) !important;
      }
    }
  }

  .ant-select-focused:not(.ant-select-disabled).ant-select:not(.ant-select-customize-input):not(
      .ant-select-status-error
    ),
  .ant-select:not(.ant-select-customize-input) {
    .ant-select-selector {
      border-radius: ${BORDER_RADIUS} !important;
      border: ${BORDER_COLOR};
      padding: 0 12px;
      box-shadow: none;
      // background: linear-gradient(
      //   135deg,
      //   rgba(255, 255, 255, 0.08) 0%,
      //   rgba(255, 255, 255, 0.03) 100%
      // ) !important;
      // box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 1px 0 0 rgba(255, 255, 255, 0.1) inset;
    }
  }

  .ant-select-selection-placeholder {
    color: ${({ disabled }) => (disabled ? 'var(--ds-gray-5)' : 'var(--ds-gray-3)')} !important;
  }

  .ant-select-disabled {
    .ant-select-selector {
      border-color: var(--ds-blue-6);
    }
  }

  .ant-select-single,
  .ant-select-selector {
    height: ${INPUT_HEIGHT};
  }

  .ant-select-single:not(.ant-select-customize-input) {
    .ant-select-selector,
    .ant-select-selection-search,
    .ant-select-selection-search-input {
      height: ${INPUT_HEIGHT};
    }
  }

  // drop-list
  &.select-dropdown {
    .ant-select-dropdown {
      .ant-select-item,
      .ant-select-tree-treenode-switcher-close {
        // hover
        &:hover {
          background-color: var(--dropdown-hover);
        }
      }
    }

    // Dropdown component styles (using same styling as Select)
    .ant-dropdown-menu {
      padding: 8px;
      border-radius: ${BORDER_RADIUS};
      z-index: 1050 !important;
      ${({ width }) => width && `width: ${width}px`};

      .ant-dropdown-menu-item {
        color: var(--primary-color);
        padding: 7px 8px;
        border-radius: ${BORDER_RADIUS};
        cursor: pointer;

        &:hover {
          background-color: var(--dropdown-hover);
        }

        &.ant-dropdown-menu-item-selected {
          color: var(--primary) !important;
          background-color: var(--ds-blue-8) !important;
          font-weight: 600 !important;

          .ant-dropdown-menu-title-content {
            font-weight: 600 !important;
          }

          .option_label {
            font-weight: 600 !important;
          }

          .option_label.typography-body-1 {
            font-weight: 600 !important;
          }

          .option.option-selected .option_label.typography-body-1 {
            font-weight: 600 !important;
          }
        }

        &.ant-dropdown-menu-item-disabled {
          color: var(--ds-gray-5);
          cursor: not-allowed;
        }

        // Selected option content styling
        .option-selected {
          color: var(--primary) !important;
          background-color: var(--ds-blue-8) !important;
          border-radius: ${BORDER_RADIUS};
          font-weight: 600 !important;

          .option_label {
            font-weight: 600 !important;
            color: var(--primary) !important;
          }
        }
      }
    }

    // Extra specific selector for the exact HTML structure
    .ant-dropdown-menu-item.ant-dropdown-menu-item-selected
      .ant-dropdown-menu-title-content
      .option.option-selected
      .option_label.typography-body-1 {
      font-weight: 700 !important;
      color: var(--primary) !important;
    }

    // Even more aggressive selectors to override typography classes
    .ant-dropdown-menu-item.ant-dropdown-menu-item-selected {
      .ant-dropdown-menu-title-content,
      .option,
      .option-selected,
      .option_label,
      .typography-body-1,
      div,
      span {
        font-weight: 700 !important;
      }
    }

    // Nuclear option - target any text inside selected items
    .ant-dropdown-menu-item-selected * {
      font-weight: 700 !important;
    }
  }

  .ant-select-dropdown {
    padding: 8px;
    border-radius: ${BORDER_RADIUS};
    z-index: 1050 !important;
    // ${({ width }) => width && `width: ${width}px`}; // what is max width?

    // item
    .ant-select-item,
    .ant-select-tree-treenode-switcher-close {
      color: var(--primary-color);
      padding: 7px 8px;
      border-radius: ${BORDER_RADIUS};
      cursor: pointer;

      &.ant-select-item-option-disabled {
        cursor: default;
      }

      // selected item
      &.ant-select-item-option-selected {
        color: ${({ multiple }) => (!multiple ? 'var(--primary)' : 'var(--primary-color)')};
        background-color: ${({ multiple }) => (!multiple ? 'var(--ds-blue-8)' : 'transparent')};

        .option_label {
          font-weight: ${({ multiple }) => (multiple ? 'var(--regular)' : 'var(--semibold)')};
        }
      }
    }
  }

  // close tag
  .chip-icon.pointer {
    &:hover {
      background-color: var(--primary);
      background-color: var(--ds-blue-7);
      border-radius: 4px;

      svg,
      svg > path {
        fill: var(--primary);
      }
    }
  }

  // placeholder
  .ant-select-selection-placeholder {
    color: var(--hint-color);
    font-weight: var(--regular);
    font-size: 13px;
    inset-inline-start: unset;
    inset-inline-end: unset;
  }

  // clear-button
  .ant-select-clear {
    margin: -8px 27px 0 0;
    width: 16px;
    height: 16px;
    position: absolute;

    &:before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      border-radius: 50%;
      z-index: -1;
    }
  }

  // workaround for chips, TODO: remove when chips will be remove from select
  .selected {
    background-color: transparent !important;
    font-size: 13px;
  }

  // advanced-dropdown styles
  &.advanced-dropdown {
    // search-input
    .ant-select-selection-search {
      margin-inline-start: 0;
    }

    // selected-item
    .ant-select-selection-item {
      background-color: transparent;
    }

    // clear-button
    .ant-select-clear {
      margin-right: 9px !important;
    }
  }

  // participants-selector styles
  &.participants-selector {
    .ant-select-selector {
      padding: 1.5px 8px !important;

      .participants-tag,
      .ant-select-selection-overflow-item-rest {
        display: flex;
        align-items: center;
        color: var(--primary-color);
        border: 1px solid var(--ds-gray-6);
        font-size: 12px;
        padding: 2px 7px;
        border-radius: 4px;
        margin-right: 6px;
        height: 22px;

        // disabled
        &.disabled {
          /* background-color: var(--ds-gray-5); */
          /* background-color: green; */
          /* color: var(--white); */
        }
      }

      .ant-select-selection-overflow-item-rest {
        padding: 2px 8px;

        .ant-select-selection-item {
          background: none;
          padding: 0;
          margin: 0;
        }
      }
    }

    .ant-tree-select-dropdown .ant-select-tree .ant-select-tree-switcher:before {
      display: none;
    }
  }
  .ant-dropdown-menu-item-selected {
    .option_label {
      font-weight: bold !important;
    }
  }
  .option {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .left_icon {
    display: flex;
    align-items: center;
  }
`
